/**
 * AI Reports Component
 * 
 * Natural language report generation using AI
 */

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Card, Form, Button, Row, Col, Alert, Spinner, Badge, ListGroup, Modal, Table, Dropdown } from 'react-bootstrap';
import { BsStars, BsRobot, BsSend, BsLightbulb, BsFileText, BsDownload, BsBarChart, BsEye, BsPlay, BsThreeDotsVertical, BsCheck, BsX } from 'react-icons/bs';
import { toast } from 'react-hot-toast';
import { ErrorBoundary, ServiceStatus, ReportingFallback } from './shared';
import { 
    fetchTemplates, 
    generateReportFromTemplate, 
    generateAIReport,
    interpretAIPrompt,
    fetchAISuggestions,
    fetchAIExamples,
    selectTemplates, 
    selectTemplatesLoading,
    selectAISuggestions,
    selectAIExamples,
    selectInterpretedPrompt,
    selectAIReportsLoading,
    selectAIReportsError
} from '../../redux/slices/reportingSlice';
import reportingService from '../../services/reportingService';

const AIReports = () => {
    const dispatch = useDispatch();
    
    // Redux state
    const templates = useSelector(selectTemplates);
    const templatesLoading = useSelector(selectTemplatesLoading);
    const suggestions = useSelector(selectAISuggestions);
    const examples = useSelector(selectAIExamples);
    const interpretedPrompt = useSelector(selectInterpretedPrompt);
    const aiLoading = useSelector(selectAIReportsLoading);
    const aiError = useSelector(selectAIReportsError);
    
    // Local state
    const [query, setQuery] = useState('');
    const [lastResult, setLastResult] = useState(null);
    const [error, setError] = useState(null);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [exportFormat, setExportFormat] = useState('pdf');

    // Load AI suggestions, examples, and templates on component mount
    useEffect(() => {
        dispatch(fetchAISuggestions());
        dispatch(fetchAIExamples());
        dispatch(fetchTemplates({ limit: 20, is_active: true }));
    }, [dispatch]);
    
    // Update available templates when templates change
    useEffect(() => {
        if (templates && templates.length > 0) {
            setAvailableTemplates(templates.filter(t => t.is_active));
        }
    }, [templates]);

    // Update local error state when Redux error changes
    useEffect(() => {
        if (aiError) {
            setError(aiError);
        } else {
            setError(null);
        }
    }, [aiError]);

    const handleGenerateReport = async () => {
        if (!query.trim()) {
            setError('Please enter a report description');
            return;
        }

        setError(null);

        try {
            // First, interpret the prompt to suggest templates
            const interpretResult = await dispatch(interpretAIPrompt({
                prompt: query.trim(),
                context: {
                    timestamp: new Date().toISOString(),
                    user_role: 'healthcare_professional',
                    available_templates: availableTemplates.map(t => t.id)
                }
            }));

            if (interpretResult.meta.requestStatus === 'fulfilled') {
                const interpretedData = interpretResult.payload.data;
                
                // If confidence is high and template is suggested, show template selection
                if (interpretedData.confidence_score > 0.7 && interpretedData.suggested_template) {
                    setShowTemplateModal(true);
                    return;
                }
            }
            
            // Generate report directly if no template match or low confidence
            const reportResult = await dispatch(generateAIReport({
                prompt: query.trim(),
                context: {
                    timestamp: new Date().toISOString(),
                    user_role: 'healthcare_professional'
                },
                export_format: exportFormat
            }));

            if (reportResult.meta.requestStatus === 'fulfilled') {
                setLastResult(reportResult.payload.data);
                setQuery(''); // Clear the input after successful generation
                toast.success('AI report generated successfully');
            } else {
                setError(reportResult.payload || 'Failed to generate AI report');
            }
        } catch (error) {
            setError('AI service is currently unavailable. Please try again later.');
        }
    };
    
    // Handle template-based generation
    const handleTemplateGeneration = async (useTemplate = true) => {
        setShowTemplateModal(false);
        
        try {
            if (useTemplate && interpretedPrompt?.suggested_template) {
                // Generate using selected template
                const template = availableTemplates.find(t => t.id === interpretedPrompt.suggested_template);
                if (template) {
                    const reportRequest = {
                        template_id: template.id,
                        filters: {
                            conditions: [],
                            logic_operator: 'and',
                            date_range: {
                                start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                                end_date: new Date().toISOString().split('T')[0]
                            }
                        },
                        export_format: exportFormat,
                        parameters: {
                            name: `AI Generated: ${template.name}`,
                            description: `Generated from query: "${query}"`,
                            ai_generated: true,
                            original_query: query.trim(),
                            ai_confidence: interpretedPrompt.confidence_score
                        }
                    };
                    
                    const response = await dispatch(generateReportFromTemplate(reportRequest));
                    if (response.meta.requestStatus === 'fulfilled') {
                        setLastResult({
                            ...interpretedPrompt,
                            report_id: response.payload?.id,
                            template_used: template.name,
                            generation_method: 'template'
                        });
                        setQuery('');
                        toast.success(`Report generated using template: ${template.name}`);
                    } else {
                        throw new Error('Template-based generation failed');
                    }
                }
            } else {
                // Generate without template (direct AI)
                const reportResult = await dispatch(generateAIReport({
                    prompt: query.trim(),
                    context: {
                        timestamp: new Date().toISOString(),
                        user_role: 'healthcare_professional',
                        force_custom: true
                    },
                    export_format: exportFormat
                }));
                
                if (reportResult.meta.requestStatus === 'fulfilled') {
                    setLastResult({
                        ...reportResult.payload.data,
                        generation_method: 'custom'
                    });
                    setQuery('');
                    toast.success('Custom AI report generated successfully');
                } else {
                    throw new Error(reportResult.payload || 'Failed to generate AI report');
                }
            }
        } catch (error) {
            setError('Failed to generate report. Please try again.');
        }
    };

    const handleExampleClick = (example) => {
        setQuery(example.prompt);
    };

    const handleSuggestionClick = (suggestion) => {
        setQuery(suggestion.title + ': ' + suggestion.description);
    };

    const handleDownloadReport = async (reportId) => {
        try {
            const response = await reportingService.downloadReport(reportId);
            if (response.success) {
                // Create download link for the blob
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `ai_report_${reportId}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            setError('Failed to download report');
        }
    };

    return (
        <ErrorBoundary fallbackMessage="Unable to load AI reports. Please refresh the page or contact support.">
            <ServiceStatus 
                fallbackComponent={<ReportingFallback />}
                showStatus={true}
                autoRetry={false}
            >
                <Container fluid className="p-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="text-primary fw-bold mb-1">
                                <BsRobot className="me-2" />
                                AI Reports
                            </h2>
                            <p className="text-muted mb-0">
                                Generate reports using natural language with AI assistance
                            </p>
                        </div>
                        {/* <Badge bg="success" className="fs-6">
                            <BsStars className="me-1" />
                            Powered by Mistral AI
                        </Badge> */}
                    </div>

                    <Row>
                        <Col lg={8}>
                            {/* Main AI Query Interface */}
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body>
                                    <h5 className="d-flex align-items-center mb-3">
                                        <BsSend className="me-2 text-primary" />
                                        Natural Language Query
                                    </h5>
                                    
                                    <Form.Group className="mb-3">
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            placeholder="Describe the report you want to generate... (e.g., 'Show all diabetic patients who missed their medication this week')"
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                            disabled={aiLoading}
                                        />
                                    </Form.Group>

                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted">Export Format</Form.Label>
                                                <Form.Select
                                                    value={exportFormat}
                                                    onChange={(e) => setExportFormat(e.target.value)}
                                                    disabled={aiLoading}
                                                    size="sm"
                                                >
                                                    <option value="pdf">PDF Document</option>
                                                    <option value="excel">Excel Spreadsheet</option>
                                                    <option value="csv">CSV File</option>
                                                    <option value="json">JSON Data</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <div className="d-flex justify-content-between align-items-center">
                                        <small className="text-muted">
                                            Use natural language to describe what data you need
                                        </small>
                                        <Button 
                                            variant="primary" 
                                            onClick={handleGenerateReport}
                                            disabled={aiLoading || !query.trim()}
                                        >
                                            {aiLoading ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Generating...
                                                </>
                                            ) : (
                                                <>
                                                    <BsStars className="me-2" />
                                                    Generate Report
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Error Display */}
                            {error && (
                                <Alert variant="danger" className="mb-4">
                                    <strong>Error:</strong> {error}
                                </Alert>
                            )}

                            {/* Results Display */}
                            {lastResult && (
                                <Card className="border-0 shadow-sm mb-4">
                                    <Card.Body>
                                        <h5 className="d-flex align-items-center justify-content-between mb-3">
                                            <span>
                                                <BsFileText className="me-2 text-success" />
                                                Generated Report
                                            </span>
                                            {lastResult.generation_method && (
                                                <Badge bg={lastResult.generation_method === 'template' ? 'primary' : 'secondary'}>
                                                    {lastResult.generation_method === 'template' ? 'Template-based' : 'Custom AI'}
                                                </Badge>
                                            )}
                                        </h5>
                                        
                                        <div className="mb-3">
                                            <strong>Interpretation:</strong>
                                            <p className="text-muted mb-2">{lastResult.interpretation}</p>
                                        </div>

                                        {lastResult.template_used && (
                                            <div className="mb-3">
                                                <strong>Template Used:</strong> 
                                                <Badge bg="info" className="ms-2">{lastResult.template_used}</Badge>
                                            </div>
                                        )}

                                        {lastResult.confidence_score && (
                                            <div className="mb-3">
                                                <strong>AI Confidence:</strong>
                                                <Badge bg={lastResult.confidence_score > 0.8 ? 'success' : lastResult.confidence_score > 0.6 ? 'warning' : 'danger'} className="ms-2">
                                                    {Math.round(lastResult.confidence_score * 100)}%
                                                </Badge>
                                                {lastResult.confidence_score > 0.8 && (
                                                    <small className="text-success ms-2">High confidence match</small>
                                                )}
                                            </div>
                                        )}

                                        {lastResult.report_id && (
                                            <div className="d-flex gap-2 mb-3">
                                                <Button 
                                                    variant="primary" 
                                                    size="sm"
                                                    onClick={() => handleDownloadReport(lastResult.report_id)}
                                                >
                                                    <BsDownload className="me-1" />
                                                    Download Report
                                                </Button>
                                                <Button 
                                                    variant="outline-secondary" 
                                                    size="sm"
                                                    onClick={() => window.open(`/reports/${lastResult.report_id}`, '_blank')}
                                                >
                                                    <BsEye className="me-1" />
                                                    View Report
                                                </Button>
                                            </div>
                                        )}

                                        {lastResult.alternative_suggestions && lastResult.alternative_suggestions.length > 0 && (
                                            <div className="mb-3">
                                                <strong>Alternative Interpretations:</strong>
                                                <ul className="mt-2">
                                                    {lastResult.alternative_suggestions.map((alternative, index) => (
                                                        <li key={index} className="text-muted small">{alternative}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {lastResult.suggestions && lastResult.suggestions.length > 0 && (
                                            <div className="mt-3">
                                                <strong>AI Suggestions for Better Results:</strong>
                                                <ul className="mt-2">
                                                    {lastResult.suggestions.map((suggestion, index) => (
                                                        <li key={index} className="text-muted small">{suggestion}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            )}
                        </Col>

                        <Col lg={4}>
                            {/* Available Templates */}
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body>
                                    <h6 className="d-flex align-items-center justify-content-between mb-3">
                                        <span>
                                            <BsBarChart className="me-2 text-primary" />
                                            Available Templates
                                        </span>
                                        <Badge bg="secondary">{availableTemplates.length}</Badge>
                                    </h6>
                                    {templatesLoading ? (
                                        <div className="text-center py-3">
                                            <Spinner animation="border" size="sm" />
                                            <p className="small text-muted mt-2">Loading templates...</p>
                                        </div>
                                    ) : availableTemplates.length > 0 ? (
                                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                            {availableTemplates.slice(0, 5).map((template) => (
                                                <div key={template.id} className="template-quick-item p-2 mb-2 border rounded bg-light">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div className="flex-grow-1">
                                                            <div className="fw-semibold text-sm">{template.name}</div>
                                                            <Badge bg="primary" size="sm">{template.category}</Badge>
                                                        </div>
                                                        <Button 
                                                            variant="outline-primary" 
                                                            size="sm"
                                                            onClick={() => {
                                                                setSelectedTemplate(template);
                                                                setQuery(`Generate a ${template.name.toLowerCase()} report`);
                                                            }}
                                                            title="Use this template"
                                                        >
                                                            <BsPlay />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                            {availableTemplates.length > 5 && (
                                                <div className="text-center mt-2">
                                                    <small className="text-muted">+{availableTemplates.length - 5} more templates available</small>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-center py-3">
                                            <BsBarChart className="h4 text-muted" />
                                            <p className="small text-muted">No templates available</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                            
                            {/* AI Suggestions */}
                            {suggestions && suggestions.length > 0 && (
                                <Card className="border-0 shadow-sm mb-4">
                                    <Card.Body>
                                        <h6 className="d-flex align-items-center mb-3">
                                            <BsLightbulb className="me-2 text-warning" />
                                            Smart Suggestions
                                        </h6>
                                        <ListGroup variant="flush">
                                            {suggestions.map((suggestion, index) => (
                                                <ListGroup.Item 
                                                    key={index}
                                                    action
                                                    onClick={() => handleSuggestionClick(suggestion)}
                                                    className="border-0 px-0"
                                                >
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <strong className="d-block small">{suggestion.title}</strong>
                                                            <small className="text-muted">{suggestion.description}</small>
                                                        </div>
                                                        <Badge bg={suggestion.priority === 'high' ? 'danger' : suggestion.priority === 'medium' ? 'warning' : 'secondary'} size="sm">
                                                            {suggestion.priority}
                                                        </Badge>
                                                    </div>
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    </Card.Body>
                                </Card>
                            )}

                            {/* Example Prompts */}
                            <Card className="border-0 shadow-sm">
                                <Card.Body>
                                    <h6 className="mb-3">Example Prompts</h6>
                                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        {examples && examples.length > 0 ? (
                                            examples.map((example, index) => (
                                                <div
                                                    key={index}
                                                    className="example-prompt p-2 mb-2 border rounded cursor-pointer"
                                                    onClick={() => handleExampleClick(example)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div className="flex-grow-1">
                                                            <div className="small fw-bold text-primary mb-1">"{example.prompt}"</div>
                                                            <small className="text-muted">{example.description}</small>
                                                        </div>
                                                        <Badge bg={example.complexity === 'hard' ? 'danger' : example.complexity === 'medium' ? 'warning' : 'success'} size="sm">
                                                            {example.complexity}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center py-3">
                                                <BsLightbulb className="h4 text-muted" />
                                                <p className="small text-muted">Loading examples...</p>
                                            </div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
                
                {/* Template Selection Modal */}
                <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title className="d-flex align-items-center">
                            <BsStars className="me-2 text-warning" />
                            AI Template Suggestion
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {interpretedPrompt && (
                            <div>
                                <Alert variant="info" className="mb-4">
                                    <div className="d-flex align-items-center mb-2">
                                        <BsRobot className="me-2" />
                                        <strong>AI Analysis</strong>
                                    </div>
                                    <p className="mb-2">{interpretedPrompt.interpretation}</p>
                                    <div className="d-flex align-items-center">
                                        <span className="me-2">Confidence:</span>
                                        <Badge bg={interpretedPrompt.confidence_score > 0.8 ? 'success' : 'warning'}>
                                            {Math.round(interpretedPrompt.confidence_score * 100)}%
                                        </Badge>
                                    </div>
                                </Alert>
                                
                                {interpretedPrompt.suggested_template && (
                                    <div>
                                        <h6 className="mb-3">Recommended Template:</h6>
                                        {(() => {
                                            const template = availableTemplates.find(t => t.id === interpretedPrompt.suggested_template);
                                            if (template) {
                                                return (
                                                    <Card className="border-primary">
                                                        <Card.Body>
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div className="flex-grow-1">
                                                                    <div className="d-flex align-items-center mb-2">
                                                                        <BsBarChart className="text-primary me-2" />
                                                                        <h6 className="mb-0">{template.name}</h6>
                                                                        <Badge bg="primary" className="ms-2">{template.category}</Badge>
                                                                    </div>
                                                                    <p className="text-muted mb-2">
                                                                        {typeof template.description === 'string' 
                                                                            ? template.description 
                                                                            : 'Template for generating healthcare reports'
                                                                        }
                                                                    </p>
                                                                    <div className="d-flex gap-2">
                                                                        <small className="text-muted">
                                                                            {Array.isArray(template.available_filters) ? template.available_filters.length : 0} filters available
                                                                        </small>
                                                                        <small className="text-muted">
                                                                            {Array.isArray(template.required_permissions) ? template.required_permissions.length : 0} permissions required
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                                <div className="text-center">
                                                                    <BsCheck className="h2 text-success" />
                                                                    <div className="small text-success">Recommended</div>
                                                                </div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>
                                )}
                                
                                {interpretedPrompt.alternative_suggestions && interpretedPrompt.alternative_suggestions.length > 0 && (
                                    <div className="mt-4">
                                        <h6 className="mb-3">Alternative Interpretations:</h6>
                                        <ul className="list-unstyled">
                                            {interpretedPrompt.alternative_suggestions.map((alternative, index) => (
                                                <li key={index} className="mb-1">
                                                    <small className="text-muted">• {alternative}</small>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => handleTemplateGeneration(false)}
                            disabled={aiLoading}
                        >
                            <BsX className="me-1" />
                            Generate Custom Report
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={() => handleTemplateGeneration(true)}
                            disabled={aiLoading}
                        >
                            {aiLoading ? (
                                <>
                                    <Spinner size="sm" className="me-1" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <BsCheck className="me-1" />
                                    Use Recommended Template
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </ServiceStatus>
        </ErrorBoundary>
    );
};

export default AIReports;